import React from 'react';
import { useUser } from '@clerk/react';
import { buildPaymentLinkUrl, stashPostPurchaseRoute } from '../utils/paymentLink';
import { T, withAlpha } from '../designTokens';
import { isSubscriptionTier, type Tier } from '../utils/types';

interface CreditBalanceProps {
  balance: number | null; // null = unlimited, or not known yet (then tier is 'free')
  tier: Tier;
  loading: boolean;
  /**
   * False hides the balance and "Buy Credits" from an account that only ever
   * bought "unlock this report" (#1776). Undefined (not known yet) keeps them.
   */
  hasCreditHistory?: boolean;
  onClick?: () => void;
}

/**
 * CreditBalance — compact nav bar widget showing the user's credit balance
 * and a "Buy Credits" link when balance is low or zero.
 */
export default function CreditBalance({ balance, tier, loading, hasCreditHistory, onClick }: CreditBalanceProps) {
  const { user } = useUser();
  const paymentLinkUrl = buildPaymentLinkUrl(user?.id);

  if (loading) {
    return (
      <div style={{
        width: '80px',
        height: '28px',
        borderRadius: '6px',
        backgroundColor: T.overlayFaint,
      }} />
    );
  }

  const badgeStyle: React.CSSProperties = {
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 600,
    color: T.cobalt300,
    backgroundColor: withAlpha(T.cobalt500, 0.15),
    borderRadius: '6px',
    fontFamily: T.fontMono,
    letterSpacing: '0.03em',
  };

  // Unlimited accounts (env-var bypass, admins): a badge, no balance. An
  // unresolved fetch also reports null but keeps tier 'free', so it falls
  // through to the pill as before.
  if (balance === null && tier !== 'free') {
    return <span style={badgeStyle}>UNLIMITED</span>;
  }

  // Subscribers still spend credits on ad-hoc scans (#1316), so they get
  // the plan badge beside the balance pill rather than instead of it.
  const planBadge = isSubscriptionTier(tier)
    ? <span style={badgeStyle}>{tier.toUpperCase()}</span>
    : null;

  // A one-time report buyer never learned what a credit is (#1776): no
  // balance, no pack upsell. A subscriber keeps the plan badge.
  if (hasCreditHistory === false) return planBadge;

  // Issue #971: the pill used to turn red at a zero balance. Running out of
  // credits is a purchase decision, not a mistake — and this chip is
  // persistent navigation that sits on every page, so an error tint here
  // contradicts the offer surface the scanner shows for the same state. One
  // neutral treatment at every balance; the cobalt Buy Credits link below is
  // the affordance.
  const pill = (
    <span style={{
      padding: '4px 10px',
      fontSize: '12px',
      fontWeight: 600,
      color: T.gray200,
      backgroundColor: T.overlaySoft,
      borderRadius: '6px',
      fontFamily: T.fontMono,
      whiteSpace: 'nowrap',
    }}>
      {balance ?? 0} credit{balance !== 1 ? 's' : ''}
    </span>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {planBadge}
      {/* Balance pill — clickable when onClick provided */}
      {onClick ? (
        <button
          onClick={onClick}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            borderRadius: '6px',
            lineHeight: 0,
          }}
          aria-label="Open billing"
        >
          {pill}
        </button>
      ) : pill}

      {/* Buy Credits link — shown when balance is low */}
      {balance !== null && balance <= 5 && paymentLinkUrl && (
        <a
          href={paymentLinkUrl}
          onClick={stashPostPurchaseRoute}
          style={{
            padding: '4px 10px',
            fontSize: '12px',
            fontWeight: 600,
            color: T.white,
            backgroundColor: T.cobalt600,
            borderRadius: '6px',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          Buy Credits
        </a>
      )}
    </div>
  );
}
